import { useInfiniteQuery } from '@tanstack/react-query';
import { concat, isEmpty } from 'lodash';
import { useEffect, useRef } from 'react';
import { queryClient } from 'src/components/queryProvider';
import { fetchWeekdayDayparts } from 'src/hooks';
import { generateQuery, requestCCApi } from 'src/utils';
import { queryKeys } from 'src/utils/queryKeys';

import { mapUserActions } from './helper';

/* 
  Keep this hook to be automatically re-fetched, then we don't need to invalidate the query key in other requests.
*/
export const useGetAllUserActions = (filters: string[] = []) => {
  const previousFiltersRef = useRef<string[] | null>(null);

  useEffect(() => {
    const currentFilters = JSON.stringify(filters);
    const previousFilters = JSON.stringify(previousFiltersRef.current);

    if (
      previousFiltersRef.current !== null &&
      previousFilters !== currentFilters
    ) {
      queryClient.removeQueries(
        queryKeys.userActions.pagination(previousFiltersRef.current),
      );
    }

    previousFiltersRef.current = filters;
  }, [filters]);

  return useInfiniteQuery(
    queryKeys.userActions.pagination(filters),
    ({ pageParam = 1 }) => fetchAllUserActions(pageParam, filters),
    {
      enabled: !isEmpty(filters),
      getNextPageParam: (lastPage, allPages) => {
        const currentPage = lastPage ? allPages.length : 1;
        const totalPages = lastPage ? lastPage?.totalPages : 1;

        return currentPage < totalPages ? currentPage + 1 : undefined;
      },
      staleTime: 0,
      refetchOnWindowFocus: false,
    },
  );
};

const fetchAllUserActions = async (page: number, filters: string[] = []) => {
  const response = await requestCCApi({
    url: `/userActions${generateQuery(
      [],
      concat(filters, [`count=true`, `page=${page}`]),
    )}`,
  });

  const weeksDayparts = await queryClient.fetchQuery({
    queryKey: queryKeys.weekdayDayparts.all(),
    queryFn: fetchWeekdayDayparts,
    staleTime: Infinity,
  });

  return mapUserActions(response, weeksDayparts);
};
